import { DEFAULT_MEDIUM_URL, DEFAULT_MEDIUM, getModelOnly } from "./artwork-model-utils";
import { getSignedUrl } from "./artwork-utils";
import configs from "./configs";
import { proxiedUrlFor } from "./media-url-utils";
import { blobToFile } from "./media-utils";
import { processThumbnail } from "./thumbnails";

async function getScale(imageUrl) {
  const textureLoader = new THREE.TextureLoader();
  imageUrl = proxiedUrlFor(imageUrl);
  const texture = await textureLoader.loadAsync(imageUrl);
  const textureWidth = texture.image.width;
  const textureHeight = texture.image.height;

  const aspectRatio = textureWidth / textureHeight;

  // Set the scaling factor for x and y based on the aspect ratio
  let scaleX, scaleY;
  if (aspectRatio > 1) {
    scaleX = aspectRatio;
    scaleY = 1;
  } else {
    scaleX = 1;
    scaleY = 1 / aspectRatio;
  }
  return [scaleX, scaleY];
}

export async function createArtwork(el, src, mediaOptions = {}) {
  const metadataArtwork = el.components["gltf-model-plus"]?.model?.userData?.metadata;
  if (metadataArtwork) {
    el.setAttribute("artwork-toggler-menu", {});
    el.setAttribute("inspect-pivot-child-selector", { selector: ".interactable-ui" });
    el.setAttribute("artwork", {
      remoteArtwork: src,
      image: el.components["gltf-model-plus"]?.model?.userData?.metadata?.imageTexture,
      artworkInfo: el.components["gltf-model-plus"]?.model?.userData?.metadata?.artworkInfo,
      glbModel: el.components["gltf-model-plus"]?.model?.userData?.metadata?.glbModel,
      scaleX: el.components["gltf-model-plus"]?.model?.userData?.metadata?.scaleX,
      scaleY: el.components["gltf-model-plus"]?.model?.userData?.metadata?.scaleY,
      scaleZ: el.components["gltf-model-plus"]?.model?.userData?.metadata?.scaleZ,
      mediumName: el.components["gltf-model-plus"]?.model?.userData?.metadata?.mediumName,
      frame: el.components["gltf-model-plus"]?.model?.userData?.metadata?.frame,
      completed: el.components["gltf-model-plus"]?.model?.userData?.metadata?.isDigitalFile
        ? el.components["gltf-model-plus"]?.model?.userData?.metadata?.completed
        : el.components["gltf-model-plus"]?.model?.userData?.metadata?.completed,
      canBeEdited: el.components["gltf-model-plus"]?.model?.userData?.metadata?.canBeEdited,
      qrCodeId: el.components["gltf-model-plus"]?.model?.userData?.metadata?.qrCodeId,
      isDigitalFile: el.components["gltf-model-plus"]?.model?.userData?.metadata?.isDigitalFile,
      resizeOnInit: el.components["gltf-model-plus"]?.model?.userData?.metadata?.resizeOnInit,
      initialScaleX: el.components["gltf-model-plus"]?.model?.userData?.metadata?.initialScaleX,
      initialScaleY: el.components["gltf-model-plus"]?.model?.userData?.metadata?.initialScaleY,
      initialScaleZ: el.components["gltf-model-plus"]?.model?.userData?.metadata?.initialScaleZ,
      scaleFactorX: el.components["gltf-model-plus"]?.model?.userData?.metadata?.scaleFactorX,
      scaleFactorY: el.components["gltf-model-plus"]?.model?.userData?.metadata?.scaleFactorY,
      scaleFactorZ: el.components["gltf-model-plus"]?.model?.userData?.metadata?.scaleFactorZ,
      myMediaId: el.components["gltf-model-plus"]?.model?.userData?.metadata?.myMediaId
    });
  }
}

export function isValidMimeType(mimeType) {
  const validMimeTypes = [
    "image/gif",
    "image/png",
    "image/jpeg",
    "image/webp",
    "video/mp4",
    "application/pdf",
    "model/gltf-binary", // MIME type for GLB files
    "application/octet-stream" // General binary stream MIME type
  ];

  if (mimeType.startsWith("audio/") || mimeType.startsWith("image/") || mimeType.startsWith("video/")) {
    return true;
  }

  return validMimeTypes.includes(mimeType);
}

export function getMediaType(mediaUrl) {
  if (!mediaUrl) {
    console.warn("To get media type, mediaUrl must be defined");
    return null;
  }

  const extension = mediaUrl.split(".").pop();

  if (["gif"].includes(extension)) {
    return "gif";
  }

  if (["png", "jpg", "jpeg", "webp"].includes(extension)) {
    return "image";
  }

  if (["mp4"].includes(extension)) {
    return "video";
  }

  if (["mp3"].includes(extension)) {
    return "audio";
  }

  if (["pdf"].includes(extension)) {
    return "pdf";
  }

  if (["glb"].includes(extension)) {
    return "model";
  }

  return "image";
}

export const ANIMATIONS_DICTIONARY = {
  male_anim: "Male Animation",
  female_anim: "Female Animation",
  female_idle_anim: "Female Animation Idle",
  male_idle_anim: "Male Animation Idle"
};

export async function simulateKeyPress(character) {
  await new Promise((resolve, reject) => setTimeout(resolve, 60));
  // Create and dispatch the keydown event
  const keydownEvent = new KeyboardEvent("keydown", {
    key: character,
    code: "Key" + character.toUpperCase(), // e.g., KeyW
    keyCode: character.charCodeAt(0), // e.g., 87 for "W"
    which: character.charCodeAt(0), // for legacy browsers
    shiftKey: false,
    ctrlKey: false,
    metaKey: false
  });
  document.dispatchEvent(keydownEvent);
  await new Promise((resolve, reject) => setTimeout(resolve, 40));
  // Create and dispatch the keyup event
  const keyupEvent = new KeyboardEvent("keyup", {
    key: character,
    code: "Key" + character.toUpperCase(), // e.g., KeyW
    keyCode: character.charCodeAt(0), // e.g., 87 for "W"
    which: character.charCodeAt(0), // for legacy browsers
    shiftKey: false,
    ctrlKey: false,
    metaKey: false
  });
  document.dispatchEvent(keyupEvent);
}

export function detectInAppBrowser() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  let inAppBrowser = false;
  if (/instagram/i.test(userAgent)) {
    inAppBrowser = true;
  } else if (/twitter/i.test(userAgent)) {
    inAppBrowser = true;
  }

  if (!inAppBrowser) return;

  alert(`You are in ${inAppBrowser}`);
}

export function getFilename(url) {
  // Extract the path from the URL
  let path = new URL(url).pathname;
  // Decode the URI components
  let decodedPath = decodeURIComponent(path);
  // Split the path into components
  let pathComponents = decodedPath.split("/");
  // Get the last component (the filename)
  let filename = pathComponents[pathComponents.length - 1];
  // Remove the extension
  let filenameWithoutExtension = filename.split(".").slice(0, -1).join(".");
  return filenameWithoutExtension;
}

export function getUrlExtension(url) {
  return url.split(/[#?]/)[0].split(".").pop().trim();
}

export async function generateAndUploadThumbnailUrl({ url, folder, token, name, mimeType }) {
  const responseFile = await fetch(proxiedUrlFor(url));

  const file = await responseFile.blob();

  const thumbnailBlob = await processThumbnail(file, url, { returnBlob: true, mimeType });

  const thumbnailFile = new File([thumbnailBlob], `${name.split(".").shift()}-thumb.png`, { type: "image/png" });

  const signedUrl = await getSignedUrl({ file: thumbnailFile, token, folder, apiUrl: configs["CUSTOM_API_URL"] });

  await fetch(signedUrl.presigned_url, {
    method: "PUT",
    headers: {
      "Content-Type": "image/png",
      accept: "application/json",
      "x-ms-blob-type": "BlockBlob"
    },
    body: thumbnailFile
  });

  return signedUrl.download_url;
}

export function extractUUID(url) {
  // Regular expression to match UUID
  const uuidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

  // Find the first match of the UUID in the string
  const match = url.match(uuidRegex);

  // Return the UUID if found, otherwise return null
  return match ? match[0] : null;
}

export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const deepClone = obj => {
  return JSON.parse(JSON.stringify(obj));
};
